
import { useState, useCallback } from "react";
import { useLanguage } from "./LanguageContext";

interface ApiExampleProps {
  /** The endpoint path (e.g., "/v1/ndc/60505008100") */
  endpoint: string;
  /** HTTP method (GET, POST, etc.) */
  method?: "GET" | "POST" | "PUT" | "DELETE";
  /** Request body for POST/PUT requests */
  requestBody?: object;
  /** The JSON response to display */
  response: object;
  /** Optional title for the example */
  title?: string;
}

/**
 * ApiExample component displays code examples based on the selected language
 * (Node.js SDK or cURL) with copy functionality and a JSON response preview.
 */
export default function ApiExample({
  endpoint,
  method = "GET",
  requestBody,
  response,
  title,
}: ApiExampleProps) {
  const { language } = useLanguage();
  const [copiedRequest, setCopiedRequest] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  const baseUrl = "https://api.fhirfly.io";
  const fullUrl = `${baseUrl}${endpoint}`;

  const code =
    language === "node"
      ? generateNodeSdk(endpoint, method, requestBody)
      : generateCurl(fullUrl, method, requestBody);

  const handleCopyRequest = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedRequest(true);
      setTimeout(() => setCopiedRequest(false), 2000);
    } catch {
      fallbackCopy(code);
      setCopiedRequest(true);
      setTimeout(() => setCopiedRequest(false), 2000);
    }
  }, [code]);

  const handleCopyResponse = useCallback(async () => {
    const text = JSON.stringify(response, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedResponse(true);
      setTimeout(() => setCopiedResponse(false), 2000);
    } catch {
      fallbackCopy(text);
      setCopiedResponse(true);
      setTimeout(() => setCopiedResponse(false), 2000);
    }
  }, [response]);

  return (
    <div className="docs-api-example">
      {title && <h4 className="docs-api-example-title">{title}</h4>}

      {/* Request Code */}
      <div className="docs-api-example-section">
        <div className="docs-api-example-label">Request</div>
        <div className="docs-codetabs">
          <div className="docs-codetabs-header">
            <span className="docs-codetabs-tab docs-codetabs-tab--active">
              {language === "node" ? "Node.js" : "cURL"}
            </span>
            <CopyButton copied={copiedRequest} onClick={handleCopyRequest} />
          </div>
          <pre className="docs-codetabs-code">
            <code>{code}</code>
          </pre>
        </div>
      </div>

      {/* Response JSON — collapsible when long, so noisy output doesn't dominate */}
      <ResponseBlock
        response={response}
        copied={copiedResponse}
        onCopy={handleCopyResponse}
      />
    </div>
  );
}

function ResponseBlock({
  response,
  copied,
  onCopy,
}: {
  response: object;
  copied: boolean;
  onCopy: () => void;
}) {
  const text = JSON.stringify(response, null, 2);
  const lineCount = text.split("\n").length;
  const collapsible = lineCount > 16;

  const body = (
    <div className="docs-codetabs">
      <div className="docs-codetabs-header">
        <span className="docs-codetabs-tab docs-codetabs-tab--active">JSON</span>
        <CopyButton copied={copied} onClick={onCopy} />
      </div>
      <pre className="docs-codetabs-code" style={{ maxHeight: "400px" }}>
        <code>{text}</code>
      </pre>
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        <div className="docs-api-example-label">Response</div>
        {body}
      </div>
    );
  }

  return (
    <details className="docs-collapsible">
      <summary>
        <span className="docs-collapsible-label">Response</span>
        <span className="docs-collapsible-meta">{lineCount} lines JSON</span>
        <svg className="docs-collapsible-chev" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
      </summary>
      <div className="docs-collapsible-body">{body}</div>
    </details>
  );
}

// --- Copy button ---

function CopyButton({ copied, onClick }: { copied: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`docs-codetabs-copy${copied ? " docs-codetabs-copy--copied" : ""}`}
      title="Copy code"
    >
      {copied ? (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

// --- Code generators ---

function fallbackCopy(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function generateCurl(url: string, method: string, body?: object): string {
  let code = `curl -X ${method} "${url}"`;
  code += ` \\\n  -H "x-api-key: YOUR_API_KEY"`;

  if (body) {
    code += ` \\\n  -H "Content-Type: application/json"`;
    code += ` \\\n  -d '${JSON.stringify(body, null, 2).split("\n").join("\n  ")}'`;
  }

  return code;
}

function generateNodeSdk(endpoint: string, method: string, body?: object): string {
  const sdk = parseSdkCall(endpoint, method, body);

  if (sdk) {
    return `import { Fhirfly } from "@fhirfly-io/terminology";

const client = new Fhirfly({ apiKey: "YOUR_API_KEY" });

${sdk}`;
  }

  // Fallback: raw fetch for endpoints the SDK doesn't cover yet
  const options: string[] = [
    `method: "${method}"`,
    `headers: {\n    "x-api-key": "YOUR_API_KEY",${body ? '\n    "Content-Type": "application/json",' : ""}\n  }`,
  ];
  if (body) {
    options.push(`body: JSON.stringify(${JSON.stringify(body, null, 2).split("\n").join("\n  ")})`);
  }

  return `const response = await fetch(
  "https://api.fhirfly.io${endpoint}",
  {
    ${options.join(",\n    ")}
  }
);

const data = await response.json();
console.log(data);`;
}

/**
 * Map API endpoint paths to SDK method calls.
 */
function parseSdkCall(endpoint: string, method: string, body?: object): string | null {
  // Connectivity lookup: GET /v1/npi/{npi}/connectivity
  const connectivityMatch = endpoint.match(/^\/v1\/npi\/(\d+)\/connectivity$/);
  if (connectivityMatch && method === "GET") {
    const [, npi] = connectivityMatch;
    return `const result = await client.connectivity.lookup("${npi}");
console.log(result.provider_summary.name);
console.log(result.connectivity_targets);`;
  }

  // Single lookup: GET /v1/{api}/{code} (no query string)
  const lookupMatch = endpoint.match(/^\/v1\/(\w[\w-]*)\/([^_/?][^?]*)$/);
  if (lookupMatch && method === "GET") {
    const [, api, code] = lookupMatch;
    const prop = sdkPropName(api!);
    if (!prop) return null;

    // ICD-10 has lookupCm/lookupPcs variants
    if (api === "icd10") {
      return `const result = await client.icd10.lookup("${code}");
console.log(result.data);`;
    }

    return `const result = await client.${prop}.lookup("${code}");
console.log(result.data);`;
  }

  // Batch lookup: POST /v1/{api}/_batch
  const batchMatch = endpoint.match(/^\/v1\/(\w[\w-]*)\/_batch$/);
  if (batchMatch && method === "POST") {
    const [, api] = batchMatch;
    const prop = sdkPropName(api!);
    if (!prop) return null;

    const codes = extractBatchCodes(body);
    return `const result = await client.${prop}.lookupMany(${JSON.stringify(codes)});
console.log(result.data.results);`;
  }

  return null;
}

/** Extract the array of codes from a batch request body, regardless of key name. */
function extractBatchCodes(body?: object): string[] {
  if (!body) return ["CODE1", "CODE2"];
  for (const key of ["codes"]) {
    if (key in body) {
      return (body as Record<string, string[]>)[key]!;
    }
  }
  return ["CODE1", "CODE2"];
}

function sdkPropName(api: string): string | null {
  const map: Record<string, string> = {
    ndc: "ndc",
    npi: "npi",
    rxnorm: "rxnorm",
    loinc: "loinc",
    icd10: "icd10",
    cvx: "cvx",
    mvx: "mvx",
    snomed: "snomed",
    "fda-label": "fdaLabels",
    "fda-labels": "fdaLabels",
  };
  return map[api] ?? null;
}
